import { Component, type ErrorInfo, type ReactNode } from 'react';
import { EmptyState } from '@/lib';

type Props = {
  fallback?: (error: Error, reset: () => void) => ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
  children: ReactNode;
};

type State = { error: Error | null };

/**
 * App-shell-level error boundary. Catches render-phase exceptions and
 * surfaces them via `EmptyState variant="error"` with a retry button
 * that re-mounts the children subtree.
 *
 * Wrapped at the route level (in __root.tsx) so a screen crash doesn't
 * blank the whole shell — the AppHeader and nav stay usable while the
 * main content shows the error.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info);
    // Surface in dev console so the error is visible alongside the
    // fallback. Production wires this to Sentry (see app/telemetry.ts).
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) {
      return this.props.fallback(this.state.error, this.reset);
    }
    return (
      <EmptyState
        variant="error"
        title="Something went wrong"
        body="The screen hit an unexpected error. Retry, or open another route from the nav."
        errorCode={this.state.error.message}
        cta={{ label: 'Retry', onClick: this.reset }}
      />
    );
  }
}
